'use client';

import { cn } from '@/libs/utils';
import { TIER_CONFIGS } from '../constants';
import type { TrustTier } from '@/shared/types/RepScoreTypes';

interface ScoreCardProps {
  score: number;
  tier: TrustTier;
  /** Helper line rendered below the score/progress */
  helper: string;
  /** When provided, renders a progress bar toward this score target */
  progressToScore?: number;
}

const MAX_SCORE = 1000;

/**
 * Neutral score card shown at the top of most drawer states.
 * Displays the current RepScore, the Trust Level pill, an optional progress
 * bar, and a helper line.
 */
export function ScoreCard({ score, tier, helper, progressToScore }: ScoreCardProps) {
  const config = TIER_CONFIGS[tier];
  const progressPct =
    progressToScore !== undefined
      ? Math.min((score / progressToScore) * 100, 100)
      : null;

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-[#F8FAFC] p-4">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-end gap-1.5">
          <span className="text-[44px] font-extrabold leading-none text-[#0C0C0C]">
            {score}
          </span>
          <span className="text-[14px] font-semibold text-[#64748B]">
            / {MAX_SCORE.toLocaleString()}
          </span>
        </div>
        <div
          className={cn(
            'flex items-center justify-center rounded-full px-2.5 py-1',
            config.badgeColor
          )}
        >
          <span className="text-[11px] font-extrabold tracking-wider">
            Trust Level: {config.displayName}
          </span>
        </div>
      </div>

      {progressPct !== null && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
          <div
            className="h-full rounded-full bg-motitoBlue transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}

      <p className="text-[12px] font-medium leading-[1.4] text-[#475569]">
        {helper}
      </p>
    </div>
  );
}

export default ScoreCard;
